import React, { startTransition, useEffect, useState, useRef } from "react";
import { ChevronDown, ChevronUp, RefreshCcw } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "../../components/misc/Card";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/misc/Pagination";
import ReconfirmOverlay from "../../components/misc/ReconfirmOverlay";
import CustomToast from "../../components/misc/CustomToast";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import { DashboardData } from "../types";
import { toast } from "react-toastify";
import LoadingScreen from "../../screens/LoadingScreen";
import { Users } from "lucide-react";
import SchedulePostDialog from "../../components/post/util/SchedulePostDialog";
import { DashboardUser, UserDetails, CampaignPost } from "./types";
import {
  fetchDashboardDataAPI,
  fetchDashboardStatsAPI,
  fetchCampaignPostsAPI,
  fetchPromotionPostsAPI,
  fetchUserDetailsAPI,
  handleRemoveAPI,
  handlePauseCampaignAPI,
  handleSaveScheduleAPI,
} from "./actions";
import DashboardTable from "./DashboardTable";
import DeploymentStatusTable from "./DeploymentStatusTable";

const Dashboard: React.FC = () => {
  // Posts sorting state per campaign
  const [postsSort, setPostsSort] = useState<{
    [campaignId: string]: { column: string; direction: "asc" | "desc" };
  }>({});
  const [promotionPostsSort, setPromotionPostsSort] = useState<{
    [campaignId: string]: { column: string; direction: "asc" | "desc" };
  }>({});
  const [myBrandPostsSort, setMyBrandPostsSort] = useState<{
    [userId: string]: { column: string; direction: "asc" | "desc" };
  }>({});
  const [leadGenerationPostsSort, setLeadGenerationPostsSort] = useState<{
    [userId: string]: { column: string; direction: "asc" | "desc" };
  }>({});
  // Real API data state
  const [realUsers, setRealUsers] = useState<DashboardUser[]>([]);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const debounceTimeout = useRef<NodeJS.Timeout>();
  const abortController = useRef<AbortController>();
  const isLoadingFromSearch = useRef<boolean>(false);
  const currentRequestId = useRef<number>(0);  
  const [postsCache, setPostsCache] = useState<{
    [campaignId: string]: CampaignPost[];
  }>({});
  const [promotionPostsCache, setPromotionPostsCache] = useState<{
    [campaignId: string]: CampaignPost[];
  }>({});
  const [userDetailsCache, setUserDetailsCache] = useState<{
    [userId: string]: UserDetails;
  }>({});
  const [postsPagination, setPostsPagination] = useState<{
    [campaignId: string]: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>({});
  const [promotionPostsPagination, setPromotionPostsPagination] = useState<{
    [campaignId: string]: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>({});
  const [myBrandPagination, setMyBrandPagination] = useState<{
    [userId: string]: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>({});
  const [leadGenerationPagination, setLeadGenerationPagination] = useState<{
    [userId: string]: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>({});

  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [expandedListingId, setExpandedListingId] = useState<string | null>(
    null,
  );
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(
    null,
  );
  const [expandedPromotionId, setExpandedPromotionId] = useState<string | null>(
    null,
  );
  const [userData, setUserData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [userDetailsLoading, setUserDetailsLoading] = useState<{
    [userId: string]: boolean;
  }>({});
  const [postsLoading, setPostsLoading] = useState<{
    [campaignId: string]: boolean;
  }>({});
  const [promotionPostsLoading, setPromotionPostsLoading] = useState<{
    [campaignId: string]: boolean;
  }>({});
  const [pauseCampaignLoading, setPauseCampaignLoading] = useState<{
    [campaignId: string]: boolean;
  }>({});
  const [myBrandLoading, setMyBrandLoading] = useState<{
    [userId: string]: boolean;
  }>({});
  const [leadGenerationLoading, setLeadGenerationLoading] = useState<{
    [userId: string]: boolean;
  }>({});

  const isMobile = useIsMobile(900);

  // Remove confirmation states
  const [removeToConfirm, setRemoveToConfirm] = useState<{
    type: string;
    id: string;
    listingType?: string;
    userId?: string;
  } | null>(null);

  // Schedule dialog state
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<{
    id: string;
    platform: string;
    scheduledFor?: string;
    campaignType?: 'listing' | 'promotion' | 'myBrand' | 'leadGeneration';
  } | null>(null);

  // Filter users based on search term (now using real API data)
  const currentUsers = realUsers;
  const totalPages = paginationInfo.totalPages;

  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [tableLoading, setTableLoading] = useState(false);
  // Generic sort state for dashboard users
  const [dashboardSort, setDashboardSort] = useState<{
    column: string;
    direction: "asc" | "desc";
  }>({ column: "", direction: "asc" });

  const getErrorResponseMessage = (error: unknown) => {
    if (typeof error === "object" && error !== null && "response" in error) {
      return (error as { response?: { data?: { message?: string } } }).response?.data?.message;
    }
    return undefined;
  };   

   // Debounce search term
    useEffect(() => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      
      // Show loading whenever we have a search term (always trigger loading for search)
      if (searchTerm) {
        isLoadingFromSearch.current = true;
        setTableLoading(true);
      }
      
      debounceTimeout.current = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
      }, 300);

      return () => {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
      };
    }, [searchTerm]);

  const fetchDashboardData = async () => {
    // Generate unique request ID
    const requestId = ++currentRequestId.current;
    
    // Only set loading if not already loading from search
    if (!isLoadingFromSearch.current) {
      setTableLoading(true);
    }

    // Cancel previous request if it exists
    if (abortController.current) {
      abortController.current.abort();
    }
    
    // Create new AbortController for this request
    abortController.current = new AbortController();

    try {
      const dashboardResponse = await fetchDashboardDataAPI(
        paginationInfo,
        sortBy,
        sortDirection,
        debouncedSearchTerm,
        abortController.current.signal
      );

      // Only update state if this is still the latest request
      if (requestId === currentRequestId.current) {
        if (dashboardResponse.status === 200 ) {
          const {
            data = [],
            pagination = { page: 1, limit: 5, total: 0, totalPages: 0 }
          } = dashboardResponse.data || {};
          const { page, limit, total, totalPages } = pagination;
          setRealUsers(data);
          setPaginationInfo({ page, limit, total, totalPages });
        }
      }
    } catch (error: unknown) {
      // Only handle error if this is still the latest request
      if (requestId === currentRequestId.current) {
        // Don't show error toast if request was aborted (user typed more)
        if ((error as any)?.name !== 'AbortError' && (error as any)?.code !== 'ERR_CANCELED') {
          toast.error(
            <CustomToast
              message="Failed to fetch dashboard data. Please try again later."
              type="error"
            />,
            { autoClose: 3000 },
          );
          // Clear loading state only on real errors (not aborts).
          isLoadingFromSearch.current = false;
          setTableLoading(false);
        }
      }
    } finally {
      // Only clear loading state if this is still the latest request
      if (requestId === currentRequestId.current) {
        isLoadingFromSearch.current = false;
        setTableLoading(false);
      }
    }
  };

  const fetchDashboardStats = async () => {
    setLoading(true);

    try {
      const userCountResponse = await fetchDashboardStatsAPI();

      if (userCountResponse.status === 200) {
        setUserData(userCountResponse.data);
      }
    } catch (error: unknown) {
      toast.error(
        <CustomToast
          message="Failed to fetch dashboard data. Please try again later."
          type="error"
        />,
        { autoClose: 3000 },
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.page, sortBy, sortDirection, debouncedSearchTerm]);

  useEffect(() => {
    fetchDashboardData();
    fetchDashboardStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to fetch posts for a campaign
  const fetchCampaignPosts = async (
    campaignId: string,
    page: number = 1,
    sortColumn?: string,
    sortDirection?: "asc" | "desc",
  ) => {
    const cacheKey = `${campaignId}_${page}`;
    setPostsLoading((prev) => ({ ...prev, [campaignId]: true }));
    try {
      const response = await fetchCampaignPostsAPI(
        campaignId,
        page,
        sortColumn,
        sortDirection,
      );

      if (response.status === 200) {
        setPostsCache((prev) => ({
          ...prev,
          [cacheKey]: response.data.data.posts || [],
        }));
        setPostsPagination((prev) => ({
          ...prev,
          [campaignId]: response.data.pagination,
        }));
      }
    } catch (error) {
      console.error(`Error fetching posts for campaign ${campaignId}:`, error);
    } finally {
      setPostsLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  // Function to fetch posts for a promotion campaign
  const fetchPromotionPosts = async (
    campaignId: string,
    page: number = 1,
    sortColumn?: string,
    sortDirection?: "asc" | "desc",
  ) => {
    const cacheKey = `${campaignId}_${page}`;
    setPromotionPostsLoading((prev) => ({ ...prev, [campaignId]: true }));
    try {
      const response = await fetchPromotionPostsAPI(
        campaignId,
        page,
        sortColumn,
        sortDirection,
      );

      if (response.status === 200) {
        setPromotionPostsCache((prev) => ({
          ...prev,
          [cacheKey]: response.data.data.posts || [],
        }));
        setPromotionPostsPagination((prev) => ({
          ...prev,
          [campaignId]: response.data.pagination,
        }));
      }
    } catch (error) {
      console.error(
        `Error fetching posts for promotion campaign ${campaignId}:`,
        error,
      );
    } finally {
      setPromotionPostsLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  // Function to fetch detailed user data
  const fetchUserDetails = async (
    userId: string,
    sortColumn?: string,
    sortDirection?: "asc" | "desc",
    page: number = 1,
    leadGenPage: number = 1,
    leadGenSortColumn?: string,
    leadGenSortDirection?: "asc" | "desc"
  ) => {
    setUserDetailsLoading((prev) => ({ ...prev, [userId]: true }));

    try {
      const response = await fetchUserDetailsAPI(userId, sortColumn, sortDirection, page, leadGenPage, leadGenSortColumn, leadGenSortDirection);

      if (response.status === 200) {
        setUserDetailsCache((prev) => ({
          ...prev,
          [userId]: response.data.data,
        }));
        
        // Handle myBrand pagination if it exists in response
        if (response.data.myBrandPagination) {
          setMyBrandPagination((prev) => ({
            ...prev,
            [userId]: response.data.myBrandPagination,
          }));
        }
        
        // Handle leadGeneration pagination if it exists in response
        if (response.data.leadGenerationPagination) {
          setLeadGenerationPagination((prev) => ({
            ...prev,
            [userId]: response.data.leadGenerationPagination,
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching user details for ${userId}:`, error);
    } finally {
      setUserDetailsLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleRemove = async (
    type: string,
    id: string,
    TypeOfListing?: string,
    userId?: string,
  ) => {
    // Set appropriate loading states immediately based on type
    if (type === "listing" && userId) {
      setUserDetailsLoading((prev) => ({ ...prev, [userId]: true }));
    } else if (type === "post") {
      // Set loading for user details if userId is provided (for MyBrand posts)
      if (userId) {
        setUserDetailsLoading((prev) => ({ ...prev, [userId]: true }));
      }
      // Set loading for all currently expanded campaigns
      Object.keys(postsPagination).forEach(campaignId => {
        setPostsLoading((prev) => ({ ...prev, [campaignId]: true }));
      });
      Object.keys(promotionPostsPagination).forEach(campaignId => {
        setPromotionPostsLoading((prev) => ({ ...prev, [campaignId]: true }));
      });
    } else if (type === "promo" && userId) {
      setUserDetailsLoading((prev) => ({ ...prev, [userId]: true }));
    }

    try {
      const response = await handleRemoveAPI(type, id, TypeOfListing);

      if (response.status === 200) {
        // Handle cache updates based on type
        if (type === "listing") {
          // Optimistically remove the listing from the cache immediately
          for (const cachedUserId in userDetailsCache) {
            const userData = userDetailsCache[cachedUserId];
            if (userData?.listings?.some((listing) => listing.id === id)) {
              setUserDetailsCache((prev) => ({
                ...prev,
                [cachedUserId]: {
                  ...prev[cachedUserId],
                  listings: prev[cachedUserId].listings.filter((listing) => listing.id !== id)
                }
              }));
              // Optimistically update the noOfListing count in realUsers
              setRealUsers((prevUsers) => prevUsers.map(user =>
                user._id === cachedUserId
                  ? { ...user, noOfListing: Math.max(0, (user.noOfListing || 0) - 1) }
                  : user
              ));
              // Clear loading state after optimistic update
              setUserDetailsLoading((prev) => ({ ...prev, [cachedUserId]: false }));
              break;
            }
          }
        } else if (type === "post") {
          // Check if this is a MyBrand post by looking in userDetailsCache
          let isMyBrandPost = false;
          let myBrandUserId: string | null = null;
          
          // Check if this is a Lead Generation post by looking in userDetailsCache
          let isLeadGenerationPost = false;
          let leadGenerationUserId: string | null = null;
          
          for (const cachedUserId in userDetailsCache) {
            const userData = userDetailsCache[cachedUserId];
            if (userData?.myBrandPosts?.some((post) => post._id === id)) {
              isMyBrandPost = true;
              myBrandUserId = cachedUserId;
              break;
            }
            if (userData?.leadGenerationPosts?.some((post: any) => post._id === id)) {
              isLeadGenerationPost = true;
              leadGenerationUserId = cachedUserId;
              break;
            }
          }

          if (isMyBrandPost && myBrandUserId) {
            // For MyBrand posts, re-fetch user details to get latest data
            setUserDetailsCache((prev) => {
              const newCache = { ...prev };
              delete newCache[myBrandUserId!];
              return newCache;
            });
            fetchUserDetails(myBrandUserId, undefined, undefined, 1, 1);
            // Refresh main table to update MyBrand post counts
            fetchDashboardData();
          } else if (isLeadGenerationPost && leadGenerationUserId) {
            // For Lead Generation posts, re-fetch user details to get latest data
            setUserDetailsCache((prev) => {
              const newCache = { ...prev };
              delete newCache[leadGenerationUserId!];
              return newCache;
            });
            fetchUserDetails(leadGenerationUserId, undefined, undefined, 1, 1);
            // Refresh main table to update Lead Generation post counts
            fetchDashboardData();
          } else {
            // Re-fetch posts for all currently expanded campaigns (listing/promotion posts)
            for (const campaignId in postsPagination) {
              const currentPage = postsPagination[campaignId]?.page || 1;
              const cacheKey = `${campaignId}_${currentPage}`;

              setPostsCache((prev) => {
                const newCache = { ...prev };
                delete newCache[cacheKey];
                return newCache;
              });

              setPostsLoading((prev) => ({ ...prev, [campaignId]: true }));

              try {
                const apiResponse = await fetchCampaignPostsAPI(
                  campaignId,
                  currentPage,
                );

                if (apiResponse.status === 200) {
                  setPostsCache((prev) => ({
                    ...prev,
                    [cacheKey]: apiResponse.data.data.posts || [],
                  }));
                  setPostsPagination((prev) => ({
                    ...prev,
                    [campaignId]: apiResponse.data.pagination,
                  }));
                  if (userId) {
                  fetchUserDetails(userId, undefined, undefined, 1, 1);
                  }
                }
              } catch (error) {
                console.error(
                  `Error re-fetching posts for campaign ${campaignId}:`,
                  error,
                );
              } finally {
                setPostsLoading((prev) => ({ ...prev, [campaignId]: false }));
              }
            }

            // Check promotion posts
            for (const campaignId in promotionPostsPagination) {
              const currentPage = promotionPostsPagination[campaignId]?.page || 1;
              const cacheKey = `${campaignId}_${currentPage}`;

              setPromotionPostsCache((prev) => {
                const newCache = { ...prev };
                delete newCache[cacheKey];
                return newCache;
              });

              setPromotionPostsLoading((prev) => ({
                ...prev,
                [campaignId]: true,
              }));

              try {
                const apiResponse = await fetchPromotionPostsAPI(
                  campaignId,
                  currentPage,
                );

                if (apiResponse.status === 200) {
                  setPromotionPostsCache((prev) => ({
                    ...prev,
                    [cacheKey]: apiResponse.data.data.posts || [],
                  }));
                  setPromotionPostsPagination((prev) => ({
                    ...prev,
                    [campaignId]: apiResponse.data.pagination,
                  }));
                  if (userId) {
                    fetchUserDetails(userId, undefined, undefined, 1, 1);
                  }
                }
              } catch (error) {
                console.error(
                  `Error re-fetching promotion posts for campaign ${campaignId}:`,
                  error,
                );
              } finally {
                setPromotionPostsLoading((prev) => ({
                  ...prev,
                  [campaignId]: false,
                }));
              }
            }
          }
        } else if (type === "promo") {
          if (userId) {
            setUserDetailsCache((prev) => {
              const newCache = { ...prev };
              delete newCache[userId];
              return newCache;
            });
            fetchUserDetails(userId, undefined, undefined, 1, 1);
          }
          // Refresh main table to update promotion counts
          fetchDashboardData();
        }

        // Show success message
        const typeDisplayName = type === "promo" ? "promotion" : type;
        toast.success(
          <CustomToast
            message={`${typeDisplayName.charAt(0).toUpperCase() + typeDisplayName.slice(1)} removed successfully.`}
            type="success"
          />,
          { autoClose: 3000 },
        );
      }
    } catch (error) {
      console.error(`Error removing ${type}:`, error);

       // Check for 403 status (access denied)
      if ((error as any)?.response?.status === 403) {
        const typeDisplayName = type === "promo" ? "promotion" : type;
        toast.error(
          <CustomToast
            message={getErrorResponseMessage(error) || `You do not have access to remove this ${typeDisplayName}.`}
            type="error"
          />,
          { autoClose: 3000 },
        );
      } else {
        toast.error(
          <CustomToast
            message={`Failed to remove ${type}. Please try again.`}
            type="error"
          />,
          { autoClose: 3000 },
        );
      }
      
      // Clear loading states on error
      if (type === "listing" && userId) {
        setUserDetailsLoading((prev) => ({ ...prev, [userId]: false }));
      } else if (type === "post") {
        // Clear user loading state if userId is provided (for MyBrand/LeadGeneration posts)
        if (userId) {
          setUserDetailsLoading((prev) => ({ ...prev, [userId]: false }));
        }
        
        // Check if this is a MyBrand or Lead Generation post and clear specific loading states
        let isMyBrandOrLeadGenPost = false;
        for (const cachedUserId in userDetailsCache) {
          const userData = userDetailsCache[cachedUserId];
          if (userData?.myBrandPosts?.some((post) => post._id === id)) {
            setUserDetailsLoading((prev) => ({ ...prev, [cachedUserId]: false }));
            isMyBrandOrLeadGenPost = true;
            break;
          }
          if (userData?.leadGenerationPosts?.some((post: any) => post._id === id)) {
            setUserDetailsLoading((prev) => ({ ...prev, [cachedUserId]: false }));
            isMyBrandOrLeadGenPost = true;
            break;
          }
        }
        
        // Only clear campaign loading states if it's not a MyBrand or Lead Generation post
        if (!isMyBrandOrLeadGenPost) {
          Object.keys(postsPagination).forEach(campaignId => {
            setPostsLoading((prev) => ({ ...prev, [campaignId]: false }));
          });
          Object.keys(promotionPostsPagination).forEach(campaignId => {
            setPromotionPostsLoading((prev) => ({ ...prev, [campaignId]: false }));
          });
        }
      } else if (type === "promo" && userId) {
        setUserDetailsLoading((prev) => ({ ...prev, [userId]: false }));
      }
    }
  };

  const handlePauseCampaign = async (
    campaignId: string,
    newStatus: string,
    userId?: string,
    type?: string,
  ) => {
    setPauseCampaignLoading((prev) => ({ ...prev, [campaignId]: true }));
    try {
      await handlePauseCampaignAPI(campaignId, newStatus, type);

      toast.success(
        <CustomToast
          message={`Campaign ${newStatus === "PAUSED" ? "paused" : "resumed"} successfully.`}
          type="success"
        />,
        { autoClose: 3000 },
      );

      // Refetch posts for this campaign if it's currently expanded
      if (type === "promo") {
        const currentPage = promotionPostsPagination[campaignId]?.page || 1;
        if (promotionPostsPagination[campaignId]) {
          await fetchPromotionPosts(campaignId, currentPage);
        }
      } else {
        const currentPage = postsPagination[campaignId]?.page || 1;
        if (postsPagination[campaignId]) {
          await fetchCampaignPosts(campaignId, currentPage);
        }
      }

      // Refresh user details if userId is available
      if (userId) {
        setUserDetailsCache((prev) => {
          const newCache = { ...prev };
          delete newCache[userId];
          return newCache;
        });
        await fetchUserDetails(userId, undefined, undefined, 1, 1);
      }
    } catch (error) {
       // Check for 403 status (access denied)
    if ((error as any)?.response?.status === 403) {
      toast.error(
        <CustomToast
          message={getErrorResponseMessage(error) || "You do not have access to modify admin campaigns."}
          type="error"
        />,
        { autoClose: 3000 },
      );
    } else {
      toast.error(
        <CustomToast
          message="Failed to update campaign status."
          type="error"
        />,
        { autoClose: 3000 },
      );
    }
    } finally {
      setPauseCampaignLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  // Schedule post handlers
  const handleSchedulePost = (post: {
    id: string;
    platform: string;
    scheduledFor?: string;
    campaignType?: 'listing' | 'promotion' | 'myBrand' | 'leadGeneration';
  }) => {
    setSelectedPost(post);
    setIsScheduleDialogOpen(true);
  };

  const handleSaveSchedule = async (updatedSchedule: {
    scheduledFor: string;
  }) => {
    if (!selectedPost) return;

    // Close dialog immediately
    setIsScheduleDialogOpen(false);
    const postId = selectedPost.id;
    const campaignType = selectedPost.campaignType;
    setSelectedPost(null);

    // Set loading states for the appropriate table type only
    if (campaignType === 'promotion') {
      Object.keys(promotionPostsPagination).forEach(campaignId => {
        setPromotionPostsLoading((prev) => ({ ...prev, [campaignId]: true }));
      });
    } else if (campaignType === 'listing') {
      Object.keys(postsPagination).forEach(campaignId => {
        setPostsLoading((prev) => ({ ...prev, [campaignId]: true }));
      });
    } else if (campaignType === 'myBrand') {
      // Set loading specifically for MyBrand posts
      Object.keys(userDetailsCache).forEach(userId => {
        if (userDetailsCache[userId]?.myBrandPosts?.some(post => post._id === postId)) {
          setMyBrandLoading((prev) => ({ ...prev, [userId]: true }));
        }
      });
    } else if (campaignType === 'leadGeneration') {
      // Set loading specifically for Lead Generation posts
      Object.keys(userDetailsCache).forEach(userId => {
        if (userDetailsCache[userId]?.leadGenerationPosts?.some((post: any) => post._id === postId)) {
          setLeadGenerationLoading((prev) => ({ ...prev, [userId]: true }));
        }
      });
    }

    try {
      const response = await handleSaveScheduleAPI(
        [postId],
        updatedSchedule.scheduledFor,
      );

      if (response.status === 200) {
        // Update the local cache to reflect the change
        setPostsCache((prevCache) => {
          const updatedCache = { ...prevCache };
          Object.keys(updatedCache).forEach((key) => {
            updatedCache[key] = updatedCache[key].map((post) =>
              post._id === postId
                ? { ...post, scheduledFor: updatedSchedule.scheduledFor }
                : post,
            );
          });
          return updatedCache;
        });

        // Update promotion posts cache as well
        setPromotionPostsCache((prevCache) => {
          const updatedCache = { ...prevCache };
          Object.keys(updatedCache).forEach((key) => {
            updatedCache[key] = updatedCache[key].map((post) =>
              post._id === postId
                ? { ...post, scheduledFor: updatedSchedule.scheduledFor }
                : post,
            );
          });
          return updatedCache;
        });

        // Update MyBrand posts in userDetailsCache
        setUserDetailsCache((prevCache) => {
          const updatedCache = { ...prevCache };
          Object.keys(updatedCache).forEach((userId) => {
            if (updatedCache[userId]?.myBrandPosts) {
              updatedCache[userId] = {
                ...updatedCache[userId],
                myBrandPosts: updatedCache[userId].myBrandPosts?.map((post) =>
                  post._id === postId
                    ? { ...post, scheduledFor: updatedSchedule.scheduledFor }
                    : post,
                ) || []
              };
            }
          });
          return updatedCache;
        });

        toast.success(
          <CustomToast
            message="Post schedule updated successfully."
            type="success"
          />,
          { autoClose: 3000 },
        );
      }
    } catch (error) {
      console.error("Failed to update post schedule:", error);
     // Check for 403 status (access denied)
    if ((error as any)?.response?.status === 403) {
      toast.error(
        <CustomToast
          message={getErrorResponseMessage(error) || "You do not have access to modify admin post schedules."}
          type="error"
        />,
        { autoClose: 3000 },
      );
    } else {
      toast.error(
        <CustomToast
          message="Failed to update post schedule."
          type="error"
        />,
        { autoClose: 3000 },
      );
    }
    } finally {
      // Clear loading states for the appropriate table type only
      if (campaignType === 'promotion') {
        Object.keys(promotionPostsPagination).forEach(campaignId => {
          setPromotionPostsLoading((prev) => ({ ...prev, [campaignId]: false }));
        });
      } else if (campaignType === 'listing') {
        Object.keys(postsPagination).forEach(campaignId => {
          setPostsLoading((prev) => ({ ...prev, [campaignId]: false }));
        });
      } else if (campaignType === 'myBrand') {
        // Clear MyBrand loading and refetch user details
        Object.keys(userDetailsCache).forEach(userId => {
          if (userDetailsCache[userId]?.myBrandPosts?.some(post => post._id === postId)) {
            setMyBrandLoading((prev) => ({ ...prev, [userId]: false }));
            // Refetch user details to get updated MyBrand posts (this will use userDetailsLoading)
            fetchUserDetails(userId, undefined, undefined, 1, 1);
          }
        });
      } else if (campaignType === 'leadGeneration') {
        // Clear Lead Generation loading and refetch user details
        Object.keys(userDetailsCache).forEach(userId => {
          if (userDetailsCache[userId]?.leadGenerationPosts?.some((post: any) => post._id === postId)) {
            setLeadGenerationLoading((prev) => ({ ...prev, [userId]: false }));
            // Refetch user details to get updated Lead Generation posts
            const currentLeadGenPage = leadGenerationPagination[userId]?.page || 1;
            fetchUserDetails(userId, undefined, undefined, 1, currentLeadGenPage);
          }
        });
      }
    }
  };

  // Function to handle myBrand pagination
  const handleMyBrandPagination = async (userId: string, page: number) => {
    const currentSort = myBrandPostsSort[userId];
    
    // Clear cache and fetch new page
    setUserDetailsCache((prevCache) => {
      const newCache = { ...prevCache };
      delete newCache[userId];
      return newCache;
    });
    
    await fetchUserDetails(
      userId,
      currentSort?.column,
      currentSort?.direction,
      page,
      1
    );
  };

  // Function to handle lead generation pagination
  const handleLeadGenerationPagination = async (userId: string, page: number) => {
    // Clear cache and fetch new page
    setUserDetailsCache((prevCache) => {
      const newCache = { ...prevCache };
      delete newCache[userId];
      return newCache;
    });
    
    // Get current myBrand page to maintain its state
    const currentMyBrandPage = myBrandPagination[userId]?.page || 1;
    await fetchUserDetails(userId, undefined, undefined, currentMyBrandPage, page);
  };

  // Unified sort handler for all tables
  const handleTableSort = (
    column: string,
    type: "dashboard" | "listing" | "promotion" | "myBrand" | "leadGeneration",
    campaignId?: string,
  ) => {
   if (type === "dashboard") {
    const newDirection =
      dashboardSort.column === column
        ? dashboardSort.direction === "asc"
          ? "desc"
          : "asc"
        : "asc";
    
    // Batch all state updates in a single transition
    startTransition(() => {
      setDashboardSort({ column, direction: newDirection });
      setSortBy(column);
      setSortDirection(newDirection);
      setPaginationInfo((prevPage) => ({ ...prevPage, page: 1 }));
    });
    } else if (type === "listing" && campaignId) {
      setPostsSort((prev) => {
        const prevSort = prev[campaignId];
        const newDirection =
          prevSort && prevSort.column === column
            ? prevSort.direction === "asc"
              ? "desc"
              : "asc"
            : "asc";
        setPostsCache((prevCache) => {
          const newCache = { ...prevCache };
          Object.keys(newCache).forEach((key) => {
            if (key.startsWith(`${campaignId}_`)) delete newCache[key];
          });
          return newCache;
        });
        fetchCampaignPosts(
          campaignId,
          postsPagination[campaignId]?.page || 1,
          column,
          newDirection,
        );
        return { ...prev, [campaignId]: { column, direction: newDirection } };
      });
    } else if (type === "promotion" && campaignId) {
      setPromotionPostsSort((prev) => {
        const prevSort = prev[campaignId];
        const newDirection =
          prevSort && prevSort.column === column
            ? prevSort.direction === "asc"
              ? "desc"
              : "asc"
            : "asc";
        setPromotionPostsCache((prevCache) => {
          const newCache = { ...prevCache };
          Object.keys(newCache).forEach((key) => {
            if (key.startsWith(`${campaignId}_`)) delete newCache[key];
          });
          return newCache;
        });
        fetchPromotionPosts(
          campaignId,
          promotionPostsPagination[campaignId]?.page || 1,
          column,
          newDirection,
        );
        return { ...prev, [campaignId]: { column, direction: newDirection } };
      });
    } else if (type === "myBrand" && campaignId) {
      // For myBrand, campaignId is actually userId
      const userId = campaignId;
      setMyBrandPostsSort((prev) => {
        const prevSort = prev[userId];
        const newDirection =
          prevSort && prevSort.column === column
            ? prevSort.direction === "asc"
              ? "desc"
              : "asc"
            : "asc";
        
        // Clear cache and fetch with new sorting
        setUserDetailsCache((prevCache) => {
          const newCache = { ...prevCache };
          delete newCache[userId];
          return newCache;
        });
        
        const currentPage = myBrandPagination[userId]?.page || 1;
        const currentLeadGenPage = leadGenerationPagination[userId]?.page || 1;
        fetchUserDetails(userId, column, newDirection, currentPage, currentLeadGenPage);
        
        return { ...prev, [userId]: { column, direction: newDirection } };
      });
    } else if (type === "leadGeneration" && campaignId) {
      // For leadGeneration, campaignId is actually userId
      const userId = campaignId;
      setLeadGenerationPostsSort((prev) => {
        const prevSort = prev[userId];
        const newDirection =
          prevSort && prevSort.column === column
            ? prevSort.direction === "asc"
              ? "desc"
              : "asc"
            : "asc";
        
        // Clear cache and fetch with new sorting for lead generation
        setUserDetailsCache((prevCache) => {
          const newCache = { ...prevCache };
          delete newCache[userId];
          return newCache;
        });
        
        const currentMyBrandPage = myBrandPagination[userId]?.page || 1;
        const currentLeadGenPage = leadGenerationPagination[userId]?.page || 1;
        // Pass lead generation sorting parameters
        fetchUserDetails(userId, undefined, undefined, currentMyBrandPage, currentLeadGenPage, column, newDirection);
        
        return { ...prev, [userId]: { column, direction: newDirection } };
      });
    }
  };

  // Unified sort icon renderer for all tables
  const renderTableSortIcon = (
    column: string,
    type: "dashboard" | "listing" | "promotion" | "myBrand" | "leadGeneration",
    campaignId?: string,
  ) => {
    let sort;
    if (type === "dashboard") sort = dashboardSort;
    else if (type === "listing" && campaignId) sort = postsSort[campaignId];
    else if (type === "promotion" && campaignId)
      sort = promotionPostsSort[campaignId];
    else if (type === "myBrand" && campaignId)
      sort = myBrandPostsSort[campaignId];
    else if (type === "leadGeneration" && campaignId)
      sort = leadGenerationPostsSort[campaignId];
    if (!sort || sort.column !== column) return null;
    return sort.direction === "asc" ? (
      <ChevronUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ChevronDown className="ml-1 h-4 w-4 inline" />
    );
  };

  return (
    <>
      <div className="p-6 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {loading && <LoadingScreen />}
        <Card className="max-w-80 mt-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold text-primary">
              {userData?.totalUsers}
            </div>
            <p className="text-xs text-muted-foreground">
              {userData?.percent} ({userData?.lastMonthUsers}) from last month
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="p-6">
        <Card>
          <CardContent>
            <DeploymentStatusTable />
          </CardContent>
          </Card>
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle className="flex-1 text-center">
                Admin Dashboard
              </CardTitle>
              <button onClick={() => fetchDashboardData()}>
                <RefreshCcw className="ml-1 h-5 w-5 inline" />
              </button>
            </div>
            {/* filters */}
            <div
              className={`flex ${isMobile ? "flex-col" : "flex-row"} pt-4 gap-x-10`}
            >
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.trim() === "") {
                    setSearchTerm("");
                  } else {
                    setSearchTerm(val);
                  }
                  setPaginationInfo((prev) => ({ ...prev, page: 1 }));
                }}
                className="border rounded px-2 py-1 flex-1"
              />
            </div>
          </CardHeader>
          
          <CardContent>
            <DashboardTable
              users={currentUsers}
              tableLoading={tableLoading}
              userDetailsCache={userDetailsCache}
              userDetailsLoading={userDetailsLoading}
              postsCache={postsCache}
              promotionPostsCache={promotionPostsCache}
              postsPagination={postsPagination}
              promotionPostsPagination={promotionPostsPagination}
              myBrandPagination={myBrandPagination}
              leadGenerationPagination={leadGenerationPagination}
              postsLoading={postsLoading}
              promotionPostsLoading={promotionPostsLoading}
              myBrandLoading={myBrandLoading}
              leadGenerationLoading={leadGenerationLoading}
              pauseCampaignLoading={pauseCampaignLoading}
              expandedUserId={expandedUserId}
              setExpandedUserId={setExpandedUserId}
              expandedListingId={expandedListingId}
              setExpandedListingId={setExpandedListingId}
              expandedCampaignId={expandedCampaignId}
              setExpandedCampaignId={setExpandedCampaignId}
              expandedPromotionId={expandedPromotionId}
              setExpandedPromotionId={setExpandedPromotionId}
              fetchUserDetails={fetchUserDetails}
              fetchCampaignPosts={fetchCampaignPosts}
              fetchPromotionPosts={fetchPromotionPosts}
              handleMyBrandPagination={handleMyBrandPagination}
              handleLeadGenerationPagination={handleLeadGenerationPagination}
              handleTableSort={handleTableSort}
              renderTableSortIcon={renderTableSortIcon}
              handlePauseCampaign={handlePauseCampaign}
              handleSchedulePost={handleSchedulePost}
              setRemoveToConfirm={setRemoveToConfirm}
            />
          </CardContent>
          {totalPages > 1 && (
            <CardFooter>
              <Pagination className="w-full">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() =>
                        setPaginationInfo((prev) => ({
                          ...prev,
                          page: Math.max(prev.page - 1, 1),
                        }))
                      }
                      className={cn(
                        paginationInfo.page === 1 &&
                          "pointer-events-none opacity-50",
                      )}
                    />
                  </PaginationItem>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number | null = null;
                    if (i === 0) pageNum = 1;
                    else if (i === 4) pageNum = totalPages;
                    else if (totalPages <= 5) pageNum = i + 1;
                    else if (paginationInfo.page <= 3) pageNum = i + 1;
                    else if (paginationInfo.page >= totalPages - 2)
                      pageNum = totalPages - 4 + i;
                    else pageNum = paginationInfo.page - 1 + i;
                    if (!pageNum) {
                      return (
                        <PaginationItem key={`e-${i}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          isActive={paginationInfo.page === pageNum}
                          onClick={() =>
                            setPaginationInfo((prev) => ({
                              ...prev,
                              page: pageNum!,
                            }))
                          }
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        setPaginationInfo((prev) => ({
                          ...prev,
                          page: Math.min(prev.page + 1, totalPages),
                        }))
                      }
                      className={cn(
                        paginationInfo.page === totalPages &&
                          "pointer-events-none opacity-50",
                      )}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </CardFooter>
          )}
        </Card>

        <ReconfirmOverlay
          isOpen={Boolean(removeToConfirm)}
          title="Remove Item?"
          message="This action cannot be undone."
          //   messageTwo={`Type: ${removeToConfirm?.type}   ID: ${removeToConfirm?.id}`}
          confirmText="Remove"
          onClose={() => setRemoveToConfirm(null)}
          onConfirm={() => {
            if (removeToConfirm) {
              handleRemove(
                removeToConfirm.type,
                removeToConfirm.id,
                removeToConfirm.listingType,
                removeToConfirm.userId,
              );
              setRemoveToConfirm(null);
            }
          }}
        />

        {selectedPost && (
          <SchedulePostDialog
            isOpen={isScheduleDialogOpen}
            onClose={() => {
              setIsScheduleDialogOpen(false);
              setSelectedPost(null);
            }}
            post={selectedPost}
            onSave={handleSaveSchedule}
          />
        )}
      </div>
    </>
  );
};

export default Dashboard;
